import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    Output
} from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { SbPendingButtonIndicatorComponent } from '../sb-pending-button-indicator/sb-pending-button-indicator.component';
import { TablerIconsModule } from 'angular-tabler-icons';
import { AppConfigQuery } from '../../../core/state/app-config/app-config.query';
import { fadeInOut } from '../../animations/fade-in-out.animation';
const DEFAULT_ICONS = {
    ART: 'palette',
    BANK: 'building-bank',
    BOND: 'certificate',
    BOX: 'Box',
    BRIEFCASE: 'briefcase',
    CASH_ACCOUNT: 'cash',
    COMMERCIAL_REAL_ESTATE: 'building-estate',
    COMPANY: 'building-factory-2',
    EQUITY: 'building-factory-2',
    JEWELRY: 'diamond',
    HOME: 'home',
    LOAN: 'square-rounded-percentage',
    ORGANISATION: 'building-factory-2',
    PERSON: 'user-circle',
    PE_CONVERTIBLE: 'building-factory-2',
    PRIVATE_ASSET: 'diamond',
    PRIVATE_EQUITY: 'building-factory-2',
    PRIVATE_FUND: 'building-bank',
    SECURITIES_ACCOUNT: 'certificate',
    TAG: 'tag',
    UNKNOWN: 'diamond',
    VEHICLE: 'car',
    ERROR: 'alert-triangle',
    NOT_SET: 'help-hexagon',
    UNKNOWN_FLAG: 'flag-question',
    // Sector icons
    BASIC_MATERIALS: 'droplet',
    CONSUMER_CYCLICAL: 'car',
    FINANCIAL_SERVICES: 'building-bank',
    REAL_ESTATE: 'building-community',
    CONSUMER_DEFENSIVE: 'shopping-cart',
    HEALTHCARE: 'first-aid-kit',
    UTILITIES: 'bulb',
    COMMUNICATION_SERVICES: 'satellite',
    ENERGY: 'bolt',
    INDUSTRIALS: 'building-factory',
    TECHNOLOGY: 'device-desktop',
    // Bond sector icons
    GOVERNMENT: 'building-bank',
    MUNICIPAL: 'building-community',
    CORPORATE: 'building-bank',
    SECURITIZED: 'cash',
    CASH_AND_EQUIVALENTS: 'cash',
    DERIVATIVE: 'adjustments-dollar'
} as const;

export type logoIcons = keyof typeof DEFAULT_ICONS;

@Component({
    selector: 'sb-company-logo',
    standalone: true,
    imports: [
        CommonModule,
        SbPendingButtonIndicatorComponent,
        NgOptimizedImage,
        TablerIconsModule
    ],
    template: `
        <div [ngStyle]="{ width: imageSize + 'px', height: imageSize + 'px' }">
            <ng-container *ngIf="!showOnlyCountryFlag; else flagOnly">
                <img
                    *ngIf="imageSrc"
                    class="sb-company-logo sb-company-item"
                    [width]="imageSize"
                    [height]="imageSize"
                    [attr.src]="imageSrc.src"
                    (load)="onImageLoad()"
                    (error)="onImageError($event, imageSrc.type)"
                    [class.opacity-100]="show === 'logo'"
                    [class.opacity-0]="show !== 'logo'"
                />
                <div
                    @fadeInOut
                    *ngIf="show === 'fallback'"
                    class="sb-company-logo-fallback sb-company-item d-flex align-items-center justify-content-center"
                    [style.height]="imageSize + 'px'"
                    [style.width]="imageSize + 'px'"
                    [style.--background]="color?.background || null"
                    [style.--color]="color?.foreground || null"
                >
                    <tabler-icon
                        [class.sb-icon-xs]="size === 'md'"
                        [class.sb-icon-sm]="size === 'lg'"
                        [name]="icon"
                    ></tabler-icon>
                </div>
                <i
                    @fadeInOut
                    *ngIf="hideCountryFlag !== true && countryCode"
                    class="sb-country-flag sb-company-item fp fp-square fp-{{
                        countryCode | lowercase
                    }} {{ size }}"
                ></i>
            </ng-container>
            <ng-template #flagOnly>
                <i
                    class="sb-company-logo sb-company-logo-round fp fp-square fp-{{
                        countryCode
                    }}"
                    [style.width]="imageSize + 'px'"
                    [style.height]="imageSize + 'px'"
                ></i>
                <div
                    class="sb-company-logo-fallback sb-company-item"
                    [style.height]="imageSize + 'px'"
                    [style.width]="imageSize + 'px'"
                    [class.visible]="show === 'fallback'"
                >
                    <tabler-icon
                        [class.sb-icon-xs]="size === 'md'"
                        [class.sb-icon-sm]="size === 'lg'"
                        [name]="icon"
                    ></tabler-icon>
                </div>
            </ng-template>
        </div>
    `,
    styleUrls: ['./sb-company-logo.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [fadeInOut]
})
export class SbCompanyLogoComponent {
    get color(): typeof this._color {
        return this._color;
    }

    @Input() set color(value: typeof this._color) {
        this._color = value;
    }
    private _color: { foreground: string; background: string } = null;
    @Input() companyLogoUrl: string = null; // url to the image to show
    @Input() companyWebsite: string = null; // website to try to load the image for
    @Input() countryCode: string = null; // country code to show the flag for
    @Input() size: 'md' | 'lg' = 'md'; // size of the icon
    @Input() type: logoIcons = null; // type of icon to show if no image is available
    @Input() hideCountryFlag = false; // hide the country flag icon
    @Input() showOnlyCountryFlag: boolean = false; // show only the country flag icon, fills entire space
    @Input() stockTicker: string = null; // stock ticker to try to load the image for, currently supports NYSE and NASDAQ tickers
    @Input() sources = { logoUrl: true, website: true, ticker: true }; // sources to try to load the image from

    show: 'fallback' | 'logo' = 'fallback';

    @Output() errorChange = new EventEmitter<ErrorEvent>();

    constructor(private readonly config: AppConfigQuery) {}

    get imageSize(): number {
        return this.size === 'lg' ? 32 : 23;
    }

    get imageSrc() {
        return this.resolveImageUrl();
    }

    resolveImageUrl(): { src: string; type: keyof typeof this.sources } | null {
        const logoUrl = this.companyLogoUrl?.trim();
        let website = this.companyWebsite?.trim();
        const ticker = this.stockTicker?.trim();
        const url = this.config.get()?.sharedResourceBaseUrl;
        if (logoUrl && this.sources.logoUrl) {
            return { src: logoUrl, type: 'logoUrl' };
        } else if (website && this.sources.website) {
            try {
                website = new URL(website).hostname;
            } catch (_) {
                // If the input is not a valid URL, assume it's a domain
            }
            website = website.replace(/^www\./, '');
            if (website.length === 0) {
                return null;
            }
            return { src: `${url}/api/brand/v1/logo?search=${website}`, type: 'website' };
        } else if (ticker && this.sources.ticker) {
            return { src: `${url}/api/brand/v1/logo?ticker=${ticker}`, type: 'ticker' };
        } else {
            return null;
        }
    }

    onImageLoad(): void {
        this.show = 'logo';
    }

    onImageError(event: ErrorEvent, source: keyof typeof this.sources): void {
        // if the image fails to load, set the source to false which will prevent it from being loaded again
        this.sources[source] = false;

        // if all sources have failed, show the fallback icon
        if (!this.sources.logoUrl && !this.sources.website && !this.sources.ticker) {
            this.show = 'fallback';
            this.errorChange.emit(event);
        }
    }

    get icon(): string {
        return DEFAULT_ICONS[this.type] || 'briefcase';
    }
}
